import React from "react";
import { Box, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { CleAttente, EtatAttente, niveauUrgence } from "../../navigation/attente";
import { Indicateur3 } from "../../components/charte/TroisBarres";
import { BoutonSecondaire } from "../../components/charte/Boutons";
import { COULEUR_DOMAINE, Domaine } from "../../theme/tribeTheme";

/* « À faire maintenant » : une ligne par type de décision en attente, la
   plus urgente en premier. Urgence en 3 barres selon l'ancienneté de
   l'élément le plus ancien (navigation/attente.ts). Partagé par l'Accueil et
   la page d'accueil d'« À traiter ». */

const LIGNES: { cle: CleAttente; titre: string; phrase: string; bouton: string; chemin: string; domaine: Domaine }[] = [
  { cle: "lieux", titre: "Lieux à valider", domaine: "lieux",
    phrase: "Lieux envoyés par les Spotters : les valider crédite leurs Tips.",
    bouton: "Valider les lieux", chemin: "/a-traiter/lieux" },
  { cle: "reponses", titre: "Réponses aux quêtes", domaine: "quetes",
    phrase: "Collectes de quêtes en attente : la validation se fait dans chaque quête.",
    bouton: "Voir les réponses", chemin: "/a-traiter/lieux?onglet=reponses" },
  { cle: "quetes", titre: "Quêtes à valider", domaine: "quetes",
    phrase: "Quêtes et demandes clients à relire avant publication.",
    bouton: "Relire les quêtes", chemin: "/a-traiter/quetes" },
  { cle: "retraits", titre: "Retraits", domaine: "argent",
    phrase: "Demandes de retrait des Spotters à décider ou à verser.",
    bouton: "Traiter les retraits", chemin: "/argent/retraits" },
];

const jours = (d: Date) => Math.max(0, Math.floor((Date.now() - d.getTime()) / 86400000));

export default function AFaire({ attente }: { attente: EtatAttente }) {
  const navigate = useNavigate();
  const lignes = LIGNES
    .map(l => ({ ...l, ligne: attente.lignes[l.cle] }))
    .filter(l => (l.ligne?.nombre ?? 0) > 0)
    .sort((a, b) => niveauUrgence(b.ligne!.plusAncien) - niveauUrgence(a.ligne!.plusAncien) || b.ligne!.nombre - a.ligne!.nombre);

  if (lignes.length === 0) {
    return (
      <Box sx={{ border: "1px solid var(--c-border)", borderRadius: "8px", p: 2.5, background: "var(--c-surface)" }}>
        <Typography sx={{ fontSize: 14, color: "var(--c-text-secondary)" }}>
          {attente.charge ? "Rien n'attend de décision pour l'instant." : "Lecture des files en attente…"}
        </Typography>
      </Box>
    );
  }

  return (
    <Box component="ul" sx={{ listStyle: "none", p: 0, m: 0, border: "1.5px solid var(--c-text-primary)", borderRadius: "8px", background: "var(--c-surface)", overflow: "hidden" }}>
      {lignes.map((l, i) => {
        const n = niveauUrgence(l.ligne!.plusAncien);
        const age = l.ligne!.plusAncien
          ? `plus ancien : ${jours(l.ligne!.plusAncien) === 0 ? "aujourd'hui" : `il y a ${jours(l.ligne!.plusAncien)} j`}`
          : "ancienneté inconnue";
        return (
          <Box component="li" key={l.cle} sx={{
            display: "grid", gridTemplateColumns: { xs: "56px 1fr", md: "72px 1fr auto auto" }, alignItems: "center", gap: { xs: 1.5, md: 2.5 },
            px: 2.25, py: 1.75, borderTop: i ? "1px solid var(--c-border)" : "none",
            borderLeft: `6px solid ${COULEUR_DOMAINE[l.domaine]}`,
          }}>
            <Typography sx={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 28, lineHeight: 1 }}>
              {l.ligne!.nombre.toLocaleString("fr-FR")}
            </Typography>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontWeight: 700, fontSize: 15 }}>
                {l.titre}
                {l.ligne!.detail && <Typography component="span" sx={{ fontWeight: 400, fontSize: 13, color: "var(--c-text-secondary)" }}> · {l.ligne!.detail}</Typography>}
              </Typography>
              <Typography sx={{ fontSize: 13.5, color: "var(--c-text-secondary)" }}>{l.phrase}</Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, gridColumn: { xs: "2", md: "auto" } }}>
              <Indicateur3 niveau={n} libelle="Urgence" detail={age} />
              <Typography sx={{ fontSize: 12, color: "var(--c-text-secondary)", whiteSpace: "nowrap" }}>{age}</Typography>
            </Box>
            <Box sx={{ gridColumn: { xs: "2", md: "auto" } }}>
              <BoutonSecondaire onClick={() => navigate(l.chemin)} sx={{ whiteSpace: "nowrap" }}>{l.bouton}</BoutonSecondaire>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}
